import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, TextField, Avatar, CircularProgress, ImageList, ImageListItem, Dialog, DialogTitle, DialogActions
} from '@mui/material';
import { collection, query, orderBy, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { AuthContext } from './Auth';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    let d;
    if (ts.toDate) d = ts.toDate();
    else if (ts.seconds) d = new Date(ts.seconds * 1000);
    else d = new Date(ts);
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) {
    return '';
  }
}

export default function Feed({ channelId, channelOwnerId }) {
  const { authUser, firestoreInstance, storageInstance } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, post: null });
  const [fullImage, setFullImage] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    console.log('Feed: authUser', authUser, 'storageInstance', storageInstance);
  }, [authUser, storageInstance]);

  useEffect(() => {
    if (!firestoreInstance || !channelId) return;
    const q = query(
      collection(firestoreInstance, 'channels', channelId, 'feed'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setPosts(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, [firestoreInstance, channelId]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files]);
    // Generate previews
    const newPreviews = await Promise.all(files.map(file => {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    }));
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const handleRemoveImage = idx => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePost = async () => {
    if (!text.trim() && images.length === 0) return;
    setUploading(true);
    let imageUrls = [];
    if (images.length > 0 && storageInstance) {
      console.log('Uploading images:', images);
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        try {
          console.log('Uploading image:', img);
          if (!(img instanceof Blob)) {
            console.error('Not a Blob/File:', img);
            continue;
          }
          const imgRef = ref(storageInstance, `feed_images/${channelId}/${Date.now()}_${img.name}`);
          await uploadBytes(imgRef, img);
          const url = await getDownloadURL(imgRef);
          imageUrls.push(url);
          console.log('Uploaded image URL:', url);
        } catch (err) {
          console.error('Image upload failed:', err);
        }
      }
    } else {
      console.log('No images to upload or storageInstance missing', images, storageInstance);
    }
    await addDoc(collection(firestoreInstance, 'channels', channelId, 'feed'), {
      text: text.trim(),
      images: imageUrls,
      createdBy: authUser?.user?.uid,
      createdByName: authUser?.user?.displayName || '',
      createdByPhoto: authUser?.user?.photoURL || '',
      createdAt: serverTimestamp(),
    });
    setText('');
    setImages([]);
    setImagePreviews([]);
    setUploading(false);
  };

  const handleDelete = async (post) => {
    setDeleteDialog({ open: true, post });
  };
  const confirmDelete = async () => {
    const post = deleteDialog.post;
    if (post.images && storageInstance) {
      for (const url of post.images) {
        try {
          const path = decodeURIComponent(url.split('/o/')[1].split('?')[0]);
          await deleteObject(ref(storageInstance, path));
        } catch {}
      }
    }
    await deleteDoc(doc(firestoreInstance, 'channels', channelId, 'feed', post.id));
    setDeleteDialog({ open: false, post: null });
  };
  const cancelDelete = () => setDeleteDialog({ open: false, post: null });

  const canDelete = (post) => authUser?.user?.uid === post.createdBy || authUser?.user?.uid === channelOwnerId;

  return (
    <Box sx={{ mb: 4 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        {authUser?.user ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Avatar src={authUser?.user?.photoURL || ''} />
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Share something..."
                value={text}
                onChange={e => setText(e.target.value)}
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                sx={{ mb: 1 }}
                disabled={uploading}
              />
              {imagePreviews.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  {imagePreviews.map((src, idx) => (
                    <Box key={idx} sx={{ position: 'relative' }}>
                      <img src={src} alt="preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                      <IconButton size="small" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: '#fff8' }} onClick={() => handleRemoveImage(idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                />
                <IconButton onClick={() => fileInputRef.current.click()} color="primary">
                  <AddPhotoAlternateIcon />
                </IconButton>
                {images.length > 0 && (
                  <Typography variant="caption">{images.length} image(s) selected</Typography>
                )}
                <Button
                  variant="contained"
                  onClick={handlePost}
                  disabled={uploading || (!text.trim() && images.length === 0)}
                  sx={{ ml: 'auto' }}
                >
                  {uploading ? <CircularProgress size={20} /> : 'Post'}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary" align="center">You must be logged in to post.</Typography>
        )}
      </Paper>
      {loading ? (
        <CircularProgress />
      ) : (
        posts.map(post => (
          <Paper key={post.id} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar src={post.createdByPhoto} sx={{ mr: 1 }} />
              <Typography variant="subtitle2" sx={{ mr: 1 }}>{post.createdByName || 'User'}</Typography>
              <Typography variant="caption" color="text.secondary">{formatTimestamp(post.createdAt)}</Typography>
              {canDelete(post) && (
                <IconButton size="small" color="error" sx={{ ml: 'auto' }} onClick={() => handleDelete(post)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Typography variant="body1" sx={{ mb: post.images?.length ? 1 : 0, whiteSpace: 'pre-line' }}>{post.text}</Typography>
            {post.images && post.images.length > 0 && (
              <ImageList cols={Math.min(3, post.images.length)} rowHeight={120} sx={{ mb: 1 }}>
                {post.images.map((img, idx) => (
                  <ImageListItem key={idx}>
                    <img
                      src={img}
                      alt="feed-img"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                      onClick={() => setFullImage(img)}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Paper>
        ))
      )}
      <Dialog open={deleteDialog.open} onClose={cancelDelete}>
        <DialogTitle>Delete Post?</DialogTitle>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!fullImage}
        onClose={() => setFullImage(null)}
        maxWidth={false}
        PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.85)' } }}
      >
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <IconButton
            onClick={() => setFullImage(null)}
            sx={{ position: 'absolute', top: 16, right: 16, color: '#fff', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, zIndex: 1400 }}
            size="large"
            aria-label="Close"
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          {fullImage && (
            <img
              src={fullImage}
              alt="full"
              style={{ maxHeight: '90vh', maxWidth: '90vw', borderRadius: 8, boxShadow: '0 4px 32px #0008' }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
